import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

class LaydownWatchManual extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-manual-open]');
    this.panel = this.querySelector('.ld-manual__panel');
    this.scrim = this.querySelector('.ld-manual__scrim');
    this.body = this.querySelector('.ld-manual__body');
    this.document = this.querySelector('[data-manual-document]');
    this.zoom = 1;
    this.mobileQuery = matchMedia('(max-width: 749px)');
    this.controller = new AbortController();
    this.animations = [];
    this.state = 'closed';
    const options = { signal: this.controller.signal };
    this.mobileQuery.addEventListener('change', () => {
      if (this.mobileQuery.matches) this.setZoom(1);
    }, options);
    this.trigger.addEventListener('click', () => this.open(), options);
    this.trigger.addEventListener('pointerenter', () => this.prepareImage(), options);
    this.trigger.addEventListener('focus', () => this.prepareImage(), options);
    this.imageObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.prepareImage();
        this.imageObserver.disconnect();
      }
    });
    this.imageObserver.observe(this.trigger);
    this.dialog.addEventListener('click', event => {
      if (event.target.closest('[data-manual-dismiss]')) this.close();
      if (event.target.closest('[data-manual-zoom-in]')) this.setZoom(this.zoom + 0.5);
      if (event.target.closest('[data-manual-zoom-out]')) this.setZoom(this.zoom - 0.5);
      if (event.target.closest('[data-manual-zoom-reset]')) this.setZoom(1);
      const anchor = event.target.closest('[data-manual-anchor]');
      if (!anchor) return;
      event.preventDefault();
      const target = this.querySelector(anchor.getAttribute('href'));
      if (!target) return;
      target.focus({ preventScroll: true });
      this.body.scrollTo({ top: target.offsetTop - this.body.offsetTop, behavior: this.reducedMotion ? 'instant' : 'smooth' });
    }, options);
    this.dialog.addEventListener('cancel', event => {
      event.preventDefault();
      this.close();
    }, options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }, options);
    this.dialog.addEventListener('close', () => this.finishClose(), options);
    this.panel.addEventListener('touchstart', event => this.startSwipe(event), { ...options, passive: true });
    this.panel.addEventListener('touchmove', event => this.moveSwipe(event), { ...options, passive: false });
    this.panel.addEventListener('touchend', () => this.endSwipe(), options);
    this.panel.addEventListener('touchcancel', () => this.endSwipe(true), options);
    this.body.addEventListener('pointerdown', event => this.startPan(event), options);
    this.body.addEventListener('pointermove', event => this.movePan(event), options);
    this.body.addEventListener('pointerup', event => this.endPan(event), options);
    this.body.addEventListener('pointercancel', event => this.endPan(event), options);
    this.body.addEventListener('lostpointercapture', event => this.endPan(event), options);
    this.body.addEventListener('dragstart', event => {
      if (this.zoom > 1 && this.document?.contains(event.target)) event.preventDefault();
    }, options);
    window.addEventListener('blur', () => this.endPan(), options);
    document.addEventListener('shopify:block:select', event => {
      if (event.target === this || this.contains(event.target)) this.open();
    }, options);
    document.addEventListener('shopify:block:deselect', event => {
      if (event.target === this || this.contains(event.target)) this.close();
    }, options);
  }

  get reducedMotion() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }

  startPan(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.zoom <= 1 || this.state !== 'open') return;
    if (!this.document?.contains(event.target)) return;
    event.preventDefault();
    this.pan = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: this.body.scrollLeft,
      top: this.body.scrollTop
    };
    this.body.classList.add('is-panning');
    this.body.setPointerCapture(event.pointerId);
  }

  movePan(event) {
    if (!this.pan || event.pointerId !== this.pan.pointerId) return;
    if (event.buttons !== 1) {
      this.endPan(event);
      return;
    }
    event.preventDefault();
    this.body.scrollLeft = this.pan.left - (event.clientX - this.pan.x);
    this.body.scrollTop = this.pan.top - (event.clientY - this.pan.y);
  }

  endPan(event) {
    if (!this.pan || (event && event.pointerId !== this.pan.pointerId)) return;
    const pointerId = this.pan.pointerId;
    this.pan = null;
    this.body.classList.remove('is-panning');
    if (this.body.hasPointerCapture(pointerId)) this.body.releasePointerCapture(pointerId);
  }

  startSwipe(event) {
    this.swipe = null;
    if (this.state !== 'open' || !matchMedia('(max-width: 749px)').matches || this.zoom !== 1) return;
    if (event.touches.length !== 1 || this.body.scrollTop > 0) return;
    if (event.target.closest('button, a, input, select, textarea, iframe')) return;
    const touch = event.touches[0];
    this.swipe = { x: touch.clientX, y: touch.clientY, time: performance.now(), distance: 0, active: false };
  }

  moveSwipe(event) {
    if (!this.swipe) return;
    if (event.touches.length !== 1) {
      this.endSwipe(true);
      return;
    }
    const touch = event.touches[0];
    const distance = touch.clientY - this.swipe.y;
    const horizontal = Math.abs(touch.clientX - this.swipe.x);
    if (!this.swipe.active) {
      if (Math.max(Math.abs(distance), horizontal) < 8) return;
      if (distance <= 0 || distance <= horizontal * 1.25 || this.body.scrollTop > 0) {
        this.swipe = null;
        return;
      }
      this.swipe.active = true;
      this.animations.forEach(animation => animation.cancel());
      this.panel.style.opacity = '1';
    }
    event.preventDefault();
    this.swipe.distance = Math.max(0, distance);
    this.panel.style.transform = `translateY(${this.swipe.distance}px)`;
    this.scrim.style.opacity = String(1 - Math.min(this.swipe.distance / this.panel.clientHeight, 1) * 0.45);
  }

  async endSwipe(cancelled = false) {
    const swipe = this.swipe;
    this.swipe = null;
    if (!swipe?.active || this.state !== 'open') return;
    const threshold = Math.min(96, this.panel.clientHeight * 0.15);
    const velocity = swipe.distance / Math.max(1, performance.now() - swipe.time);
    if (!cancelled && (swipe.distance >= threshold || (swipe.distance > 35 && velocity > 0.5))) {
      this.dismissGesture = true;
      this.close();
      return;
    }
    this.state = 'settling';
    if (await this.animateModal(true)) this.state = 'open';
  }

  setZoom(value) {
    if (!this.document) return;
    this.endPan();
    const next = this.mobileQuery.matches ? 1 : Math.min(3, Math.max(1, value));
    const centerX = (this.body.scrollLeft + this.body.clientWidth / 2) / this.zoom;
    const centerY = (this.body.scrollTop + this.body.clientHeight / 2) / this.zoom;
    this.zoom = next;
    this.body.classList.toggle('is-zoomed', next > 1);
    this.document.style.setProperty('--manual-zoom', next);
    this.body.scrollLeft = next === 1 ? 0 : centerX * next - this.body.clientWidth / 2;
    this.body.scrollTop = next === 1 ? 0 : centerY * next - this.body.clientHeight / 2;
    this.querySelector('[data-manual-zoom-level]').textContent = `${Math.round(next * 100)}%`;
    this.querySelector('[data-manual-zoom-out]').disabled = next === 1;
    this.querySelector('[data-manual-zoom-in]').disabled = next === 3;
  }

  prepareImage() {
    const image = this.querySelector('.ld-manual__image');
    if (!image) return Promise.resolve();
    image.loading = 'eager';
    return image.decode().catch(() => {});
  }

  async animateModal(opening) {
    const generation = this.generation = (this.generation || 0) + 1;
    const startPanel = getComputedStyle(this.panel);
    const panelFrom = { opacity: startPanel.opacity, transform: startPanel.transform };
    const scrimFrom = { opacity: getComputedStyle(this.scrim).opacity };
    this.animations.forEach(animation => animation.cancel());
    this.dialog.classList.add('is-animating');
    const desktop = !this.mobileQuery.matches;
    const duration = this.reducedMotion ? 0 : desktop ? (opening ? 360 : 280) : (opening ? 680 : 520);
    const options = {
      duration,
      easing: desktop ? 'cubic-bezier(.25,.1,.25,1)' : opening ? 'cubic-bezier(.16,1,.3,1)' : 'cubic-bezier(.4,0,.2,1)',
      fill: 'forwards'
    };
    const scrimAnimation = this.scrim.animate([scrimFrom, { opacity: opening ? 1 : 0 }], {
      ...options, easing: desktop ? 'ease-in-out' : options.easing
    });
    this.animations = [scrimAnimation];
    // Decode the selected responsive image before revealing it; the backdrop starts immediately.
    if (opening && this.state === 'opening') await this.prepareImage();
    if (!this.reducedMotion) await new Promise(resolve => requestAnimationFrame(resolve));
    if (this.generation !== generation || !this.isConnected) return false;
    const exitDistance = this.dismissGesture ? window.innerHeight + 32 : desktop ? 16 : 32;
    const panelAnimation = this.panel.animate([
      panelFrom,
      { opacity: opening ? 1 : 0, transform: opening ? 'translate3d(0,0,0)' : 'translate3d(0,' + exitDistance + 'px,0)' }
    ], options);
    this.animations.push(panelAnimation);
    await Promise.allSettled(this.animations.map(animation => animation.finished));
    if (this.generation !== generation) return false;
    // Keep the settled appearance without retaining completed animation layers.
    this.panel.style.opacity = opening ? '1' : '0';
    this.panel.style.transform = opening ? 'translate3d(0,0,0)' : 'translate3d(0,' + exitDistance + 'px,0)';
    this.scrim.style.opacity = opening ? '1' : '0';
    this.animations.forEach(animation => animation.cancel());
    this.animations = [];
    this.dialog.classList.remove('is-animating');
    return true;
  }

  async open() {
    if (this.state !== 'closed' || !this.isConnected) return;
    this.state = 'opening';
    this.navigationReleased = false;
    this.returnFocus = document.activeElement;
    this.previousScrollY = getScrollTop();
    this.body.scrollTop = 0;
    this.setZoom(1);
    this.dismissGesture = false;
    this.swipe = null;
    this.animations.forEach(animation => animation.cancel());
    lockScroll(this.dialog);
    this.dialog.showModal();
    scrollTo({ top: this.previousScrollY, behavior: 'instant' });
    this.trigger.setAttribute('aria-expanded', 'true');
    this.dialog.querySelectorAll('[data-manual-pdf]').forEach(frame => {
      if (!frame.hasAttribute('src')) frame.src = frame.dataset.manualPdf;
    });
    if (await this.animateModal(true)) this.state = 'open';
  }

  async close() {
    if (!this.dialog.open || this.state === 'closing') return;
    this.endPan();
    this.state = 'closing';
    this.swipe = null;
    // Keep the exit in the top layer without modal inertness or hit testing.
    if (typeof this.dialog.showPopover === 'function') {
      this.dialog.close();
      this.dialog.setAttribute('popover', 'manual');
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      this.dialog.showPopover();
      this.releaseNavigation();
    }
    if (!(await this.animateModal(false))) return;
    if (this.dialog.matches(':popover-open')) this.dialog.hidePopover();
    if (this.dialog.open) this.dialog.close();
    this.finishClose();
  }

  releaseNavigation() {
    if (this.navigationReleased) return;
    this.navigationReleased = true;
    unlockScroll(this.dialog);
    scrollTo({ top: this.previousScrollY, behavior: 'instant' });
    this.trigger.setAttribute('aria-expanded', 'false');
    if (this.isConnected) {
      const target = this.returnFocus?.isConnected ? this.returnFocus : this.trigger;
      target.focus({ preventScroll: true });
    }
  }

  finishClose() {
    if (this.dialog.open || this.dialog.matches(':popover-open') || this.state === 'closed') return;
    this.endPan();
    this.state = 'closed';
    this.generation = (this.generation || 0) + 1;
    this.animations.forEach(animation => animation.cancel());
    this.panel.style.removeProperty('transform');
    this.panel.style.removeProperty('opacity');
    this.scrim.style.removeProperty('opacity');
    this.dialog.classList.remove('is-animating');
    this.dialog.removeAttribute('popover');
    this.dialog.inert = false;
    this.dialog.removeAttribute('aria-hidden');
    this.releaseNavigation();
  }

  disconnectedCallback() {
    this.endPan();
    this.imageObserver?.disconnect();
    this.controller?.abort();
    this.controller = null;
    this.generation = (this.generation || 0) + 1;
    this.animations?.forEach(animation => animation.cancel());
    if (this.dialog?.matches(':popover-open')) this.dialog.hidePopover();
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
  }
}

if (!customElements.get('laydown-watch-manual')) customElements.define('laydown-watch-manual', LaydownWatchManual);
